import ky from 'ky'

import { getInternalIdToken } from '../auth/firebase-auth.ts'

/**
 * Put the session's bearer token on a request.
 *
 * Separated from the hook below so the header rule can be asserted without a
 * session, a fetch or a running auth module.
 *
 * @param headers - The outgoing request's headers.
 * @param token - The ID token, or null when nobody is signed in.
 */
export function applyIdTokenHeader(
  headers: Headers,
  token: string | null
): void {
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }
}

/**
 * Attach the current session's ID token.
 *
 * Async because the token is asked for at call time rather than read out of
 * Web Storage: a live Firebase user mints a fresh one and the SDK refreshes as
 * needed, so a long-lived tab never sends a token it has outlived. Reading a
 * stored copy directly is what `src/auth/session-store.ts` exists to keep to
 * one module — see `docs/decisions/0003-ui-internal-persists-one-id-token.md`.
 *
 * A refresh that rejects is swallowed. The request then goes out unauthorized
 * and the API answers 401, which is a failure the caller already handles;
 * throwing here would surface as a network error instead.
 *
 * @param headers - The outgoing request's headers.
 */
export async function attachIdToken(headers: Headers): Promise<void> {
  let token: string | null
  try {
    token = await getInternalIdToken()
  } catch {
    return
  }

  applyIdTokenHeader(headers, token)
}

export const api = ky.create({
  prefix: import.meta.env.VITE_API_BASE_URL ?? '',
  hooks: {
    beforeRequest: [
      async ({ request }) => {
        await attachIdToken(request.headers)
      }
    ]
  }
})
